/**
 * `createApiReviewsSource` — the `seed-mgmt-api` human review queue over the
 * shared `ApiClient`.
 *
 * Everything the platform DISCOVERS about an address, a contract, a token or a
 * catalog row is quarantined rather than written: the pipeline's catalog flush
 * (`pipeline-mgmt-api` `build_review_store`) and the transaction ingest's
 * discovery (`transactions-mgmt-api` `catalog_discovery`) both enqueue onto ONE
 * queue, under the default `QUARANTINE` policy, and a seed run with `review_all`
 * queues every row it assembles. Nothing leaves that queue without a person, and
 * until this port existed the only way to be that person was a CLI. A proposal
 * nobody approves is a catalog row that never exists.
 *
 * Canonical `/seed-reviews` paths are rewritten onto `internal/seed-mgmt-api`
 * (`/v0/seed/reviews`) by `RESOURCE_REWRITES`, so region routing, failover, auth
 * and envelope unwrap are all inherited from the one HTTP brain.
 *
 * ## What the wire actually carries
 *
 * `review_item_to_api` (seed-mgmt-api `review/wire.py`) is the whole shape. It is
 * read through `@authe/catalog/review`'s `reviewItemFromWire` — the ONE mapping the
 * admin portal reads the same queue through, so a renamed field breaks both apps
 * at compile time instead of rendering `undefined` in one. Three parts of it are
 * what make a decision possible rather than a coin toss:
 *
 * - `field_path` / `existing_value` / `proposed_value` — the DIFF, when the
 *   proposal is about one field of an existing row. Absent on a whole-row
 *   proposal, where `payload` is the row being proposed and there is nothing to
 *   diff against.
 * - `evidence` — `commons.review.build_evidence_view`'s projection: per-source
 *   confidence, who dissented, how stale each assertion is, and the arithmetic
 *   (`base_confidence + agreement_delta = headline_confidence`) that produced
 *   the headline. A bare 0.52 is unactionable; this is what makes it falsifiable.
 * - `reason` / `reason_detail` / `providers` / `source` — WHY it is in the queue
 *   at all (below threshold, a conflict with a human's own edit, a rotation, a
 *   quarantined provider).
 *
 * ## Paging
 *
 * `walkPages` is deliberately NOT used. That helper drains `{items,
 * next_cursor}`, and this route answers `{reviews, count, cursor}` — and more
 * importantly the queue is a work list, not a picker: draining every page of a
 * `review_all` run before drawing anything is the wrong trade. So the cursor is
 * exposed and the surface pages explicitly. The API's own warning is honoured
 * either way: a page can come back SHORT and still have more behind it (the
 * `target_kind` filter is applied after the limit), so the stop condition is a
 * null cursor and never an empty page.
 */
import { type ApiClient, unwrapData } from "@authe/fe-commons/api";
import {
  reviewItemFromWire,
  type ReviewItem,
  type ReviewStatusFilter,
  type ReviewTargetKind,
} from "@authe/catalog/review";

import { asNumber, asRecord, asString, rowsAt } from "./wire";

/** Canonical collection path; `RESOURCE_REWRITES` maps it to the service. */
export const REVIEWS_PATH = "/seed-reviews";

/**
 * How many candidates one bulk decision may carry.
 *
 * `seed_mgmt_api_shared.review.service.BULK_DECISION_LIMIT`. Bounded by the
 * Lambda timeout rather than by taste — the decisions run sequentially and an
 * approval does a validate plus a live write — so exceeding it does not fail
 * cleanly, it times out halfway with no way to tell which half landed. The
 * surface states the cap rather than silently truncating a selection.
 */
export const BULK_DECISION_LIMIT = 100;

/** One page of the queue. `cursor` null means this is the last one. */
export interface ReviewPage {
  items: ReviewItem[];
  cursor: string | null;
}

export interface ListReviewsParams {
  status?: ReviewStatusFilter;
  targetKind?: ReviewTargetKind;
  limit?: number;
  cursor?: string;
  signal?: AbortSignal;
}

/** `PromotionResult` — what one approve / reject actually did. */
export interface ReviewDecisionResult {
  reviewId: string;
  /** The resulting status, or `"missing"` when the id is unknown. */
  status: string;
  /** Whether the snapshot reached its canonical table. */
  wroteLive: boolean;
  table: string | null;
  detail: string;
}

/** `BulkDecisionResult` — one candidate's outcome inside a bulk decision. */
export interface BulkReviewResult {
  reviewId: string;
  ok: boolean;
  status: string | null;
  wroteLive: boolean | null;
  table: string | null;
  error: string | null;
  /**
   * The exception class name — `ConflictError` (somebody already decided it)
   * and `ValidationError` (the row no longer validates) mean different things to
   * a reader deciding whether to retry, and a message alone makes them guess.
   */
  errorKind: string | null;
}

/**
 * `BulkDecision` — the totals plus every item's outcome.
 *
 * The route answers **200 even for a partial batch**, which is the one thing a
 * caller must not miss: a blanket 4xx would say nothing about the items that DID
 * land, so `failed > 0` on a successful response is normal and has to be read.
 */
export interface BulkDecisionOutcome {
  decision: string;
  requested: number;
  decided: number;
  failed: number;
  results: BulkReviewResult[];
}

export interface DecideReviewInput {
  decision: "approved" | "rejected";
  notes?: string;
  /**
   * Reviewer edits applied before the live write — *approve-with-edits*.
   *
   * Re-validated server-side against the row's canonical model, with the
   * identity / key fields locked, so this cannot re-point a proposal at a
   * different row.
   */
  payload?: Record<string, unknown>;
}

export interface ReviewsSource {
  /** One page of the queue, oldest first. Drain on {@link ReviewPage.cursor}. */
  list(params?: ListReviewsParams): Promise<ReviewPage>;
  /** One candidate, with its payload, its diff and its evidence. */
  get(reviewId: string): Promise<ReviewItem>;
  /** Approve (optionally with edits) or reject one candidate. */
  decide(reviewId: string, input: DecideReviewInput): Promise<ReviewDecisionResult>;
  /** Reopen a REJECTED candidate for re-review. Approved items are refused. */
  reopen(reviewId: string, notes?: string): Promise<ReviewItem>;
  /** Save edits to a pending proposal without deciding it yet. */
  saveEdits(reviewId: string, payload: Record<string, unknown>): Promise<ReviewItem>;
  /**
   * Decide up to {@link BULK_DECISION_LIMIT} candidates in one call.
   *
   * Rejects a longer list HERE rather than sending it: the server's refusal
   * would be a 400 the reader cannot act on, and the surface can state the cap.
   */
  bulkDecide(
    reviewIds: readonly string[],
    decision: "approved" | "rejected",
    notes?: string,
  ): Promise<BulkDecisionOutcome>;
  /** Permanently drop a DECIDED candidate (queue hygiene). Needs `seed:admin`. */
  remove(reviewId: string): Promise<void>;
}

function decisionResultFromWire(raw: Record<string, unknown>): ReviewDecisionResult {
  return {
    reviewId: asString(raw.review_id),
    status: asString(raw.status, "missing"),
    wroteLive: raw.wrote_live === true,
    table: raw.table == null ? null : String(raw.table),
    detail: asString(raw.detail),
  };
}

function bulkResultFromWire(raw: Record<string, unknown>): BulkReviewResult {
  return {
    reviewId: asString(raw.review_id),
    ok: raw.ok === true,
    status: raw.status == null ? null : String(raw.status),
    wroteLive: raw.wrote_live == null ? null : raw.wrote_live === true,
    table: raw.table == null ? null : String(raw.table),
    error: raw.error == null ? null : String(raw.error),
    errorKind: raw.error_kind == null ? null : String(raw.error_kind),
  };
}

/**
 * The bulk outcome. `results` is read per item, never inferred from the totals:
 * the call answers 200 for a partial batch, so `decided`/`failed` are a summary
 * of the list and not a substitute for it.
 */
export function bulkOutcomeFromWire(raw: Record<string, unknown>): BulkDecisionOutcome {
  const results = rowsAt(raw, "results").map(bulkResultFromWire);
  return {
    decision: asString(raw.decision),
    requested: asNumber(raw.requested) ?? results.length,
    decided: asNumber(raw.decided) ?? results.filter((r) => r.ok).length,
    failed: asNumber(raw.failed) ?? results.filter((r) => !r.ok).length,
    results,
  };
}

export function createApiReviewsSource(client: ApiClient): ReviewsSource {
  const itemPath = (reviewId: string) => `${REVIEWS_PATH}/${encodeURIComponent(reviewId)}`;

  /**
   * Every decision changes what the queue lists, so the collection read is
   * dropped from cache by fragment: the list is keyed by status + kind + cursor,
   * and an exact-URL invalidate would only reach the one page the caller can
   * name — leaving the other status tabs asserting a row that has just moved.
   */
  const invalidate = (reviewId?: string): void => {
    client.invalidateMatching(REVIEWS_PATH);
    if (reviewId) client.invalidate(itemPath(reviewId));
  };

  return {
    async list({
      status = "pending",
      targetKind,
      limit,
      cursor,
      signal,
    }: ListReviewsParams = {}): Promise<ReviewPage> {
      const query: Record<string, string> = { status };
      if (targetKind) query.target_kind = targetKind;
      if (limit != null) query.limit = String(limit);
      if (cursor) query.cursor = cursor;

      const raw = asRecord(
        unwrapData(await client.get<unknown>(REVIEWS_PATH, { query, signal })),
      );
      const next = raw.cursor;
      return {
        items: rowsAt(raw, "reviews").map(reviewItemFromWire),
        // Present and non-null means there is more, EVEN IF this page came back
        // short — `target_kind` filters after the limit. A blank string is not a
        // cursor, so it is normalised to null rather than re-sent as `?cursor=`.
        cursor: typeof next === "string" && next ? next : null,
      };
    },

    async get(reviewId: string): Promise<ReviewItem> {
      const raw = asRecord(unwrapData(await client.get<unknown>(itemPath(reviewId))));
      return reviewItemFromWire(raw.review);
    },

    async decide(reviewId, input): Promise<ReviewDecisionResult> {
      const body: Record<string, unknown> = { decision: input.decision };
      if (input.notes) body.notes = input.notes;
      // Only on an approval. `payload` on a rejection is meaningless — nothing
      // is written — and sending it would record edits against a discarded row.
      if (input.payload && input.decision === "approved") body.payload = input.payload;

      const raw = asRecord(unwrapData(await client.patch<unknown>(itemPath(reviewId), body)));
      invalidate(reviewId);
      return decisionResultFromWire(asRecord(raw.result));
    },

    async reopen(reviewId, notes): Promise<ReviewItem> {
      const body: Record<string, unknown> = { decision: "reopen" };
      if (notes) body.notes = notes;
      const raw = asRecord(unwrapData(await client.patch<unknown>(itemPath(reviewId), body)));
      invalidate(reviewId);
      return reviewItemFromWire(raw.review);
    },

    async saveEdits(reviewId, payload): Promise<ReviewItem> {
      // No `decision` — the route reads that as "save edits in place".
      const raw = asRecord(
        unwrapData(await client.patch<unknown>(itemPath(reviewId), { payload })),
      );
      invalidate(reviewId);
      return reviewItemFromWire(raw.review);
    },

    async bulkDecide(reviewIds, decision, notes): Promise<BulkDecisionOutcome> {
      // Blanks dropped and duplicates collapsed BEFORE the cap is measured, the
      // same way the server does it: a repeated id would otherwise be decided
      // twice and the second attempt reported as a conflict — a failure for
      // something that did in fact happen — and would eat a slot under the cap.
      const ids = Array.from(new Set(reviewIds.map((id) => id.trim()).filter(Boolean)));
      if (ids.length === 0) {
        throw new Error("Select at least one proposal to decide.");
      }
      if (ids.length > BULK_DECISION_LIMIT) {
        throw new Error(
          `One bulk decision covers at most ${BULK_DECISION_LIMIT} proposals; ${ids.length} were selected.`,
        );
      }
      const body: Record<string, unknown> = { review_ids: ids, decision };
      if (notes) body.notes = notes;

      const raw = asRecord(unwrapData(await client.patch<unknown>(REVIEWS_PATH, body)));
      invalidate();
      return bulkOutcomeFromWire(asRecord(raw.bulk));
    },

    async remove(reviewId: string): Promise<void> {
      unwrapData(await client.delete<void>(itemPath(reviewId)));
      invalidate(reviewId);
    },
  };
}
